import React from "react";
import { useNavigate } from "react-router-dom";
import StoryLayout, { restartStory } from "./StoryLayout";
import { Plug, Clock, RotateCw } from "lucide-react";

const DMS_LOGOS = [
    "Veeva Vault",
    "SharePoint",
    "Enlyte",
    "Documentum",
    "Network shares",
    "Homegrown ECM",
];

export default function StoryPunchline() {
    const navigate = useNavigate();
    return (
        <StoryLayout
            slideNumber={8}
            eyebrow="ONCO-247 · Amendment v2.2 · Same defensibility, different cycle time"
            nextTo="/icf/extract"
            prevTo="/icf/explain?story=1"
            nextLabel="Explore ONCO-247 yourself"
            testid="story-punchline"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
                <div>
                    <div className="overline mb-4">Manual re-consent cycle</div>
                    <div className="heading text-6xl md:text-7xl font-medium tracking-tighter leading-none text-[#0A0A0A]">
                        8+ months
                    </div>
                    <div className="text-[13px] text-neutral-600 mt-4 leading-relaxed">
                        Sponsor QA · IRB · CRO · 12 site regulatory leads ·
                        3-5 business days per participant × 87 participants.
                    </div>
                </div>
                <div>
                    <div className="overline mb-4">AudRI cycle</div>
                    <div className="heading text-6xl md:text-7xl font-medium tracking-tighter leading-none text-[#C2410C]">
                        ~4 seconds
                    </div>
                    <div className="text-[13px] text-neutral-600 mt-4 leading-relaxed">
                        Extract · Execute · Explain — end to end. Every
                        participant decided. Every rule checked. Every
                        evidence chain ready.
                    </div>
                </div>
            </div>

            <div className="border-t border-hair mt-16 pt-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-center">
                    {[
                        { k: "Paper trail", v: "Identical" },
                        { k: "21 CFR 50.25 rigor", v: "Identical" },
                        { k: "Site burden", v: "Near-zero" },
                    ].map((c) => (
                        <div
                            key={c.k}
                            className="border border-hair p-5 rounded-sm bg-white"
                        >
                            <div className="overline mb-2">{c.k}</div>
                            <div className="heading text-lg font-medium tracking-tight text-[#0A0A0A]">
                                {c.v}
                            </div>
                        </div>
                    ))}
                </div>

                <blockquote className="mt-12 heading text-xl md:text-2xl tracking-tight leading-snug italic text-neutral-800 border-l-2 border-[#C2410C] pl-6 max-w-3xl">
                    You don&apos;t buy AudRI to replace your QA team. You buy
                    it so a random protocol deviation never becomes the
                    reason your amendment slips a milestone.
                </blockquote>

                <div className="mt-12 border border-hair bg-white p-6 md:p-8 rounded-sm">
                    <div className="flex items-center gap-2 overline text-[#C2410C] mb-4">
                        <Plug size={11} /> How AudRI shows up in your stack
                    </div>
                    <p className="text-[14px] text-neutral-700 leading-relaxed max-w-3xl">
                        <strong className="text-[#0A0A0A]">
                            AudRI is not a rip-and-replace.
                        </strong>{" "}
                        It integrates with your existing document management
                        system — Veeva Vault, SharePoint, Enlyte, Documentum,
                        network shares, or a homegrown ECM. Your sites and
                        sponsors keep working in the systems they already know.
                        AudRI reads across all of them and layers the compliance
                        engine on top.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {DMS_LOGOS.map((n) => (
                            <span
                                key={n}
                                className="mono text-[10px] uppercase tracking-wider px-2 py-1 border border-hair rounded-sm text-neutral-600 bg-neutral-50"
                            >
                                {n}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="mt-10 flex items-center gap-4 flex-wrap">
                    <div className="text-[12px] text-neutral-500 mono flex items-center gap-2">
                        <Clock size={12} />
                        You&apos;ve reached the end of the story. From here,
                        poke around — ONCO-247&apos;s data stays exactly as
                        you saw it.
                    </div>
                    <button
                        type="button"
                        onClick={() => restartStory(navigate)}
                        data-testid="punchline-restart"
                        className="ml-auto inline-flex items-center gap-2 border border-hair bg-white text-[#0A0A0A] text-sm px-4 py-2.5 rounded-sm hover:bg-neutral-50 transition-colors"
                    >
                        <RotateCw size={13} /> Restart story from the top
                    </button>
                </div>
            </div>
        </StoryLayout>
    );
}
